import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, AlertTriangle, AlertCircle, Info, RefreshCw } from "lucide-react";

const SEVERITY = {
  critical: {
    icon: AlertCircle,
    badge: "bg-red-100 text-red-700 border-red-300",
    border: "border-l-red-500",
  },
  warning: {
    icon: AlertTriangle,
    badge: "bg-amber-100 text-amber-700 border-amber-300",
    border: "border-l-amber-500",
  },
  info: {
    icon: Info,
    badge: "bg-slate-100 text-slate-700 border-slate-300",
    border: "border-l-slate-400",
  },
};

const WINDOW_OPTIONS = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
];

export default function DispatchAdvisorPanel({ auth, API }) {
  const [hours, setHours] = useState(168);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAdvisor = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/dispatch/recommendations?since_hours=${hours}`,
        auth(),
      );
      setData(res.data);
    } catch (e) {
      console.error("advisor fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [API, auth, hours]);

  useEffect(() => {
    fetchAdvisor();
    // Refresh every 5 minutes — advisor rarely changes faster than this.
    const t = setInterval(fetchAdvisor, 300000);
    return () => clearInterval(t);
  }, [fetchAdvisor]);

  if (!data) {
    return (
      <Card data-testid="dispatch-advisor-loading">
        <CardContent className="py-6 text-center text-xs text-slate-400">
          Loading dispatch advisor…
        </CardContent>
      </Card>
    );
  }

  const recs = data.recommendations || [];
  const critical = recs.filter(r => r.severity === "critical").length;
  const warning = recs.filter(r => r.severity === "warning").length;

  return (
    <Card data-testid="dispatch-advisor-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            Dispatch advisor
            <span className="text-[10px] text-slate-400 font-normal">
              (read-only suggestions — nothing is auto-applied)
            </span>
            {loading && (
              <RefreshCw
                className="h-3 w-3 text-slate-400 animate-spin"
                data-testid="advisor-refreshing"
              />
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {critical > 0 && (
              <Badge
                className="bg-red-100 text-red-700 border-red-200"
                data-testid="advisor-critical-count"
              >
                {critical} critical
              </Badge>
            )}
            {warning > 0 && (
              <Badge
                className="bg-amber-100 text-amber-700 border-amber-200"
                data-testid="advisor-warning-count"
              >
                {warning} warning
              </Badge>
            )}
            <div className="flex gap-1" data-testid="advisor-window-picker">
              {WINDOW_OPTIONS.map(opt => (
                <button
                  key={opt.hours}
                  onClick={() => setHours(opt.hours)}
                  className={`px-2 py-1 text-[11px] rounded border transition-colors ${
                    hours === opt.hours
                      ? "bg-amber-500 border-amber-500 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                  }`}
                  data-testid={`advisor-window-${opt.label}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {recs.length === 0 ? (
          <div
            className="text-center py-6 text-sm text-slate-500"
            data-testid="advisor-empty"
          >
            <span className="text-green-600 font-semibold">All clear.</span>{" "}
            No dispatch issues detected in the last{" "}
            {hours < 24 ? `${hours}h` : `${hours / 24}d`}.
          </div>
        ) : (
          recs.map(r => {
            const cfg = SEVERITY[r.severity] || SEVERITY.info;
            const Icon = cfg.icon;
            return (
              <div
                key={r.id}
                className={`flex items-start gap-3 border-l-4 ${cfg.border} bg-slate-50/50 rounded-r-md p-3`}
                data-testid={`advisor-rec-${r.id}`}
              >
                <Icon
                  className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                    r.severity === "critical"
                      ? "text-red-500"
                      : r.severity === "warning"
                      ? "text-amber-500"
                      : "text-slate-400"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{r.title}</p>
                    <Badge className={`text-[10px] ${cfg.badge}`}>
                      {r.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    {r.why}
                  </p>
                  <p
                    className="text-xs text-slate-700 mt-2 italic"
                    data-testid={`advisor-action-${r.id}`}
                  >
                    <strong className="not-italic text-slate-500">Suggested action:</strong>{" "}
                    {r.suggested_action}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <p className="text-[10px] text-slate-400 pt-2">
          Generated {new Date(data.generated_at).toLocaleTimeString()} · advisory only ·
          refreshes every 5 min
        </p>
      </CardContent>
    </Card>
  );
}
